import ApiService from '../../../../../services/ApiService';

export interface SkippedHoliday {
  date: string;
  name: string;
}

export interface DeliveryComputation {
  date: string;
  skippedHolidays: SkippedHoliday[];
  hadBridgeDay: boolean;
  finalWeekday: number;
  failed: boolean;
}

const previewCache = new Map<string, DeliveryComputation>();
const inFlight = new Map<string, Promise<DeliveryComputation>>();

const emptyComputation = (failed = false): DeliveryComputation => ({
  date: '',
  skippedHolidays: [],
  hadBridgeDay: false,
  finalWeekday: 0,
  failed,
});

const normalizeBaseDate = (value?: string): string => {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;

  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

export const computeDelivery = async (baseDate: string | undefined, businessDays: number): Promise<DeliveryComputation> => {
  if (!businessDays || businessDays <= 0 || isNaN(businessDays)) return emptyComputation();

  const normalizedBase = normalizeBaseDate(baseDate);
  const key = `${normalizedBase}|${businessDays}`;

  const cached = previewCache.get(key);
  if (cached) return cached;

  const pending = inFlight.get(key);
  if (pending) return pending;

  const promise = (async () => {
    try {
      const { data } = await ApiService.steelOrders.getDeliveryPreview({ baseDate: normalizedBase, businessDays });
      const computation: DeliveryComputation = {
        date: data?.date || '',
        skippedHolidays: Array.isArray(data?.skippedHolidays) ? data.skippedHolidays : [],
        hadBridgeDay: !!data?.hadBridgeDay,
        finalWeekday: Number(data?.finalWeekday) || 0,
        failed: false,
      };
      previewCache.set(key, computation);
      return computation;
    } catch {
      return emptyComputation(true);
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
};

export const computeDeliveryDate = async (baseDate: string | undefined, businessDays: number): Promise<string> => {
  const computation = await computeDelivery(baseDate, businessDays);
  return computation.date;
};

const formatDayMonth = (isoDate: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  return match ? `${match[3]}/${match[2]}` : isoDate;
};

export const buildHolidayMessage = (info: DeliveryComputation): string | null => {
  const first = info.skippedHolidays[0];
  if (!first) return null;

  const others = info.skippedHolidays.length - 1;
  const suffix = others > 0 ? ` e mais ${others}` : '';

  if (info.finalWeekday === 1) {
    return `Entrega na segunda-feira ${formatDayMonth(info.date)} — feriado no período (${first.name} ${formatDayMonth(first.date)}${suffix})`;
  }

  return `Feriado no período: ${first.name} (${formatDayMonth(first.date)})${suffix}`;
};

export const formatDeliveryDate = (isoDate: string): string => formatDayMonth(isoDate);
